import { z } from 'zod';

/**
 * @file risk-factor.schema.ts
 * @description Catálogo de fatores de risco obstétrico e o ESCOPO de cada um.
 *
 * Baseado nas diretrizes do Ministério da Saúde, simplificado (MVP). Morava
 * hardcoded em `GestationalRiskModal.tsx`, um arquivo de tela — é conhecimento
 * de domínio, e a API precisa dele para validar.
 *
 * ## O escopo pertence ao CÓDIGO, não ao registro
 *
 * Guardar o escopo em cada fator marcado permitiria o mesmo código gravado com
 * escopos diferentes em documentos diferentes. Declara-se aqui, uma vez.
 *
 * ## O critério, para quando o catálogo crescer
 *
 * > Quando a mulher engravidar de novo, isso precisa ser **reavaliado**?
 * > Se sim, é da gestação. Se já é verdade para sempre, é da mulher.
 *
 * Não é "o fato persiste?". Isoimunização Rh persiste biologicamente e ainda
 * assim é da gestação, porque o manejo se refaz a cada uma. Vulnerabilidade
 * social também persiste e é reavaliada.
 */

export const RiskFactorScopeEnum = z.enum([
  /** Da mulher: uma vez verdade, vale para todas as gestações. */
  'WOMAN',
  /** Da gestação: reavaliado a cada uma. */
  'PREGNANCY',
  /** Nem um nem outro: calculado na hora, nunca gravado. */
  'DERIVED',
]);
export type RiskFactorScope = z.infer<typeof RiskFactorScopeEnum>;

export interface RiskFactorDef {
  code: string;
  label: string;
  /** Grupo de exibição, na ordem em que a tela mostra. */
  group: 'PERSONAL' | 'HISTORY' | 'CLINICAL';
  scope: RiskFactorScope;
}

export const RISK_FACTOR_GROUP_LABELS: Record<RiskFactorDef['group'], string> = {
  PERSONAL: 'Características Individuais e Sociodemográficas',
  HISTORY: 'História Reprodutiva Anterior',
  CLINICAL: 'Condições Clínicas / Gestação Atual',
};

export const RISK_FACTORS: RiskFactorDef[] = [
  // --- Características individuais -----------------------------------------
  // Idade é DERIVED: sai de `dateOfBirth` mais a data de referência. Marcada à
  // mão ela envelhece errado — quem foi marcada aos 36 fica marcada, e quem
  // tinha 34 numa gestação tem 36 na seguinte sem ninguém atualizar.
  { code: 'age_lt_15', label: 'Idade menor que 15 anos', group: 'PERSONAL', scope: 'DERIVED' },
  { code: 'age_gt_35', label: 'Idade maior que 35 anos', group: 'PERSONAL', scope: 'DERIVED' },
  { code: 'height_lt_145', label: 'Altura menor que 1,45m', group: 'PERSONAL', scope: 'WOMAN' },
  // O peso ANTES de cada gestação é diferente, apesar de parecer da mulher.
  { code: 'weight_pre_gt_standard', label: 'Peso pré-gestacional excessivo', group: 'PERSONAL', scope: 'PREGNANCY' },
  { code: 'weight_pre_lt_45', label: 'Peso pré-gestacional menor que 45kg', group: 'PERSONAL', scope: 'PREGNANCY' },
  { code: 'social_vulnerability', label: 'Vulnerabilidade social / Baixa escolaridade', group: 'PERSONAL', scope: 'PREGNANCY' },

  // --- História reprodutiva: fatos acumulados, todos da mulher --------------
  { code: 'habitual_abortion', label: 'Abortamento habitual', group: 'HISTORY', scope: 'WOMAN' },
  { code: 'previous_perinatal_death', label: 'Morte perinatal explicada/inexplicada', group: 'HISTORY', scope: 'WOMAN' },
  { code: 'previous_preterm', label: 'Bebê prematuro anterior', group: 'HISTORY', scope: 'WOMAN' },
  { code: 'previous_preeclampsia', label: 'Pré-eclâmpsia / Eclâmpsia anterior', group: 'HISTORY', scope: 'WOMAN' },
  { code: 'previous_csection', label: 'Cesariana anterior', group: 'HISTORY', scope: 'WOMAN' },

  // --- Condições clínicas: o grupo mistura os dois escopos ------------------
  { code: 'chronic_hypertension', label: 'Hipertensão Arterial Crônica', group: 'CLINICAL', scope: 'WOMAN' },
  { code: 'diabetes_pre', label: 'Diabetes Mellitus (Pré-gestacional)', group: 'CLINICAL', scope: 'WOMAN' },
  { code: 'cardiopathy', label: 'Cardiopatias', group: 'CLINICAL', scope: 'WOMAN' },
  { code: 'gestational_diabetes', label: 'Diabetes Gestacional', group: 'CLINICAL', scope: 'PREGNANCY' },
  { code: 'multiple_pregnancy', label: 'Gestação Múltipla', group: 'CLINICAL', scope: 'PREGNANCY' },
  // A sensibilização é permanente, mas o manejo se refaz a cada gestação.
  { code: 'isoimmunization', label: 'Isoimunização Rh', group: 'CLINICAL', scope: 'PREGNANCY' },
  { code: 'bleeding', label: 'Sangramento gestacional', group: 'CLINICAL', scope: 'PREGNANCY' },
];

/**
 * `cardropathy` era o código gravado pela tela — erro de digitação de
 * `cardiopathy`. Corrigido aqui, com o alias mapeado na leitura. Não havia
 * nenhum fator gravado em produção quando a correção entrou, então o alias é
 * cinto de segurança para QA e para dado local, não migração de verdade.
 */
const CODE_ALIASES: Record<string, string> = {
  cardropathy: 'cardiopathy',
};

/** Normaliza um código legado para o canônico. */
export function normalizeRiskFactorCode(code: string): string {
  return CODE_ALIASES[code] ?? code;
}

const BY_CODE = new Map(RISK_FACTORS.map((f) => [f.code, f]));

export function riskFactorDef(code: string): RiskFactorDef | undefined {
  return BY_CODE.get(normalizeRiskFactorCode(code));
}

/** Fatores de um escopo, na ordem do catálogo. */
export function riskFactorsByScope(scope: RiskFactorScope): RiskFactorDef[] {
  return RISK_FACTORS.filter((f) => f.scope === scope);
}

/**
 * Separa uma lista de códigos marcados por onde cada um deve ser gravado.
 * Códigos desconhecidos vão para `unknown` em vez de sumirem em silêncio.
 */
export function splitRiskFactorsByScope(codes: string[]): {
  woman: string[];
  pregnancy: string[];
  derived: string[];
  unknown: string[];
} {
  const out = { woman: [] as string[], pregnancy: [] as string[], derived: [] as string[], unknown: [] as string[] };
  for (const raw of codes) {
    const code = normalizeRiskFactorCode(raw);
    const def = BY_CODE.get(code);
    if (!def) { out.unknown.push(raw); continue; }
    if (def.scope === 'WOMAN') out.woman.push(code);
    else if (def.scope === 'PREGNANCY') out.pregnancy.push(code);
    else out.derived.push(code);
  }
  return out;
}
